import * as React from "react";
import {ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState} from "react";
import {
    alpha,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Theme,
    ThemeProvider,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import TranslateSafeLoadingButton from "../../shared/translate-safe-loading-button/TranslateSafeLoadingButton";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import {useAppDispatch} from "../../../store";
import Typography from "@mui/material/Typography";
import {Trans, useTranslation} from "react-i18next";
import {IWish} from "../../../store/wish/Helper";
import ReactGA from "react-ga4";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import {HelpOutline} from "@mui/icons-material";
import {useTheme} from "@mui/material/styles";
import {validateEmail} from "../../../util/regex";
import TextField from "@mui/material/TextField";
import {formatDate, formatPrice, getSafeBackendErrorMessage} from "../../../util/utils";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../util/constants";
import {startCpayPayment} from "./submitPayment";
import {toastError} from "../../shared/toast/Toast";
import WishService from "../../../api/wishService";
import {reloadWishAfterAction} from "../../../store/wish/Slice";
import {checkWishToRemove} from "../../../store/wishlist/Slice";
import QtySelector from "../../wishes/display-wishes/common/make-a-wish-grid/qty-selector/QtySelector";
import {Swiper as SwiperType} from "swiper";
import ScrollFader from "../../shared/scroll-fader/ScrollFader";
import {metaTrackCustom} from "../../../util/metaPixel";

interface IProps {
    open: boolean;
    handleDialogClose: () => void;
    resetTicketCount: () => void;
    wish: IWish;
    count: number;
    setHasParticipated: Dispatch<SetStateAction<boolean>>;
    orderId: string;
    options: IWish[];
    activeWish?: IWish
    setCount: Dispatch<SetStateAction<any>>
    recommended: boolean
    swiperInstance?: SwiperType
}

const initialGiftState = {
    isGift: false,
    recipient_email: "",
    recipient_name: "",
    personal_message: ""
};

const BuyVouchersDialog: FC<IProps> = (props) => {
    const {i18n, t} = useTranslation();
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [isNameValid, setIsNameValid] = useState<boolean>(false);
    const [giftForm, setGiftForm] = useState(initialGiftState);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const dispatch = useAppDispatch()
    const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

    const [selectedWish, setSelectedWish] = useState<IWish>(props.options.find(w => w.max_tickets - w.tickets_count >= props.count) ?? props.wish);

    useEffect(() => {
        if (props.activeWish) {
            setSelectedWish(props.activeWish);
        }
    }, [props.activeWish, props.open])


    const handleOptionChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
        const selectedOption = props.options.find((w) => w.id === value) ?? props.wish
        if (props.count > (selectedOption.max_tickets - selectedOption.tickets_count)) {
            props.setCount(1)
        }
        setSelectedWish(selectedOption);
    };

    const steps = [t("details"), t("payment"), t("confirmation")];

    const handleOnInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === "recipient_email" && validateEmail(e.target.value)) {
            setIsEmailValid(true);
        }
        if (e.target.name === "recipient_name" && e.target.value.length > 0) {
            setIsNameValid(true);
        }
        setGiftForm((oldForm) => ({
            ...oldForm,
            [e.target.name]: e.target.value
        }));
    };

    const handleChangeGiftVoucher = () => {
        setGiftForm((oldForm) => ({
            ...oldForm,
            isGift: !oldForm.isGift,
            recipient_email: "",
            recipient_name: "",
            personal_message: ""
        }));
        setIsEmailValid(true);
        setIsNameValid(true);
    };

    const handleOnBlurEmailField = () => {
        setIsEmailValid(validateEmail(giftForm.recipient_email ?? ""));
    };

    const handleOnBlurNameField = () => {
        setIsNameValid(giftForm.recipient_name.length > 0);
    };

    const handleBuyTickets = async () => {
        ReactGA.event({
            category: "Wish",
            action: "make_a_wish_click_confirm",
            label: `Make a wish - Confirm - ${selectedWish.id}`
        });
        setLoading(true);
        const {isGift, recipient_name, recipient_email, personal_message} = giftForm;
        try {
            await startCpayPayment(
                props.orderId,
                selectedWish.id,
                props.count,
                isGift
                    ? {
                        recipient_email,
                        recipient_name,
                        personal_message
                    }
                    : {}
            );
        } catch (e: any) {
            metaTrackCustom("PurchaseAttemptFailed", {
                content_id: selectedWish.id,
                content_name: selectedWish.name_en,
                content_type: selectedWish.type,
                total_due: price,
            });
            toastError(getSafeBackendErrorMessage(e.response?.data?.detail))
        } finally {
            setLoading(false)
            WishService.fetchWishDetails(selectedWish.id)
                .then((res) => {
                    dispatch(reloadWishAfterAction(res.data));
                    dispatch(checkWishToRemove(res.data));
                })
        }
    };

    // Prices use the selected option
    const currentPrice = selectedWish.cost;
    const price = currentPrice * props.count;

    const isFormValid =
        !giftForm.isGift ||
        (validateEmail(giftForm.recipient_email) && giftForm.recipient_name.length > 0);

    const detailsStepContent = (
        <>
            {/* OPTIONS GROUP */}
            {props.options.length > 1 && (
                <FormControl component="fieldset" sx={{mb: 2, width: "100%"}} color="primary">
                    <FormLabel color="primary">{t("options")}</FormLabel>
                    <ScrollFader faderSize={30} color="secondary">
                        <Box maxHeight={200}>
                            <RadioGroup
                                name="selectedWish"
                                value={selectedWish.id}
                                onChange={handleOptionChange}
                                sx={{flexWrap: "wrap"}}
                            >
                                {props.options.map((w) => {
                                    const label = w[`option_name_${i18n.language}`] as string;
                                    const vouchersLeft = w.max_tickets - w.tickets_count;
                                    const pointsEarned = w.points_award ?? 0;

                                    return (
                                        <FormControlLabel
                                            key={w.id}
                                            value={w.id}
                                            disabled={vouchersLeft === 0 || vouchersLeft < props.count}
                                            control={
                                                <Radio
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        "&.Mui-checked": {color: theme.palette.primary.main},
                                                    }}
                                                />
                                            }
                                            label={`${label} (${t('left', {count: vouchersLeft})}, +${pointsEarned} ${t('genie_points')})`}
                                            sx={{color: theme.palette.primary.main, mr: 2}}
                                        />
                                    );
                                })}
                            </RadioGroup>
                        </Box>

                    </ScrollFader>
                </FormControl>
            )}

            {/* SUMMARY BOX */}
            <Box p={2} border={`1px solid ${theme.palette.primary.main}`}>
                <Stack divider={<Divider sx={{backgroundColor: alpha(theme.palette.primary.main, 0.7)}}/>}>
                    <Grid container justifyContent="space-between" alignItems="center" pb={0.5} gap="1em"
                          flexWrap="nowrap">
                        <Typography variant="subtitle2" color="primary">
                            {props.count} x {selectedWish[`name_${i18n.language}`]}
                        </Typography>
                        <Typography variant="subtitle2" color="primary" fontWeight={600} whiteSpace="nowrap">
                            {formatPrice(price)}
                        </Typography>
                    </Grid>
                    <Grid container justifyContent="space-between" alignItems="center" pt={0.5} gap="1em"
                          flexWrap="nowrap">
                        <Typography variant="subtitle1" color="primary" fontWeight={600}>
                            {t("total")}
                        </Typography>
                        <Typography variant="subtitle1" color="primary" fontWeight="bold" whiteSpace="nowrap">
                            {formatPrice(price)}
                        </Typography>
                    </Grid>
                </Stack>
            </Box>

            {/* GIFT TOGGLE + FIELDS */}
            <FormControlLabel
                sx={{mt: 2, mb: 1}}
                control={
                    <Checkbox
                        sx={{color: theme.palette.primary.main}}
                        value={giftForm.isGift}
                        checked={giftForm.isGift}
                        color="primary"
                        onChange={handleChangeGiftVoucher}
                        size={"small"}
                    />
                }
                label={
                    <Typography variant="body1" color="primary">
                        {t("send_as_gift")}
                        <Tooltip enterTouchDelay={0} title={t("send_voucher_as_gift_tooltip")} arrow placement="top">
                            <HelpOutline
                                sx={{width: "15px", height: "15px", p: 0, ml: 0.5}}
                                onClick={(e) => e.preventDefault()}
                            />
                        </Tooltip>
                    </Typography>
                }
            />

            {giftForm.isGift && (
                <>
                    <TextField
                        required
                        color="primary"
                        type="email"
                        fullWidth
                        name="recipient_email"
                        value={giftForm.recipient_email}
                        onChange={handleOnInputChange}
                        label={t("recipient_email")}
                        error={!isEmailValid}
                        helperText={!isEmailValid && t("email_field_not_valid")}
                        onBlur={handleOnBlurEmailField}
                    />

                    <TextField
                        color="primary"
                        type="text"
                        required
                        fullWidth
                        name="recipient_name"
                        value={giftForm.recipient_name}
                        onChange={handleOnInputChange}
                        label={t("recipient_name")}
                        error={!isNameValid}
                        helperText={!isNameValid && t("field_required")}
                        onBlur={handleOnBlurNameField}
                        sx={{my: 2}}
                    />

                    <TextField
                        fullWidth
                        color="primary"
                        value={giftForm.personal_message}
                        name="personal_message"
                        onChange={handleOnInputChange}
                        multiline
                        rows={3}
                        label={t("personalized_message")}
                    />
                </>
            )}

            <>
                <Divider sx={{backgroundColor: theme.palette.primary.main, mt: 2}}/>
                <Typography variant="body2" color="primary" mt={1}>
                    <Trans
                        i18nKey="buy_vouchers_notice"
                        values={{expiry: formatDate(selectedWish.voucher_expiry, false)}}
                        components={[
                            <Link component={RouterLink} sx={{cursor: "pointer"}} color="primary"
                                  to={RoutePaths.TERMS} target="_blank" rel="noopener noreferrer"/>
                        ]}
                    />
                </Typography>
            </>
        </>
    );

    const dialogContent = (
        <Box>
            <Stepper variant="outlined" activeStep={0} alternativeLabel sx={{mb: 4}}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            {detailsStepContent}
        </Box>
    );

    const dialogActions = (
        <Grid container justifyContent="space-between" alignItems="center">
            <QtySelector qty={props.count} setQty={props.setCount}
                         max={selectedWish.max_tickets - selectedWish.tickets_count}
                         recommended={false} size="medium"
                         swiperInstance={props.swiperInstance}/>
            <TranslateSafeLoadingButton variant="contained" size="large" loading={loading} onClick={handleBuyTickets}
                           disabled={!isFormValid}>
                {matchesSm ? t("proceed_to_payment_short") : t("proceed_to_payment")}
            </TranslateSafeLoadingButton>
        </Grid>
    );

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog
                open={props.open}
                title={t("buy_voucher", {count: props.count})}
                maxWidth="sm"
                actions={dialogActions}
                handleDialogClose={props.handleDialogClose}
                content={dialogContent}
            />
        </ThemeProvider>
    );
};

export default BuyVouchersDialog;
